/**
 * WORLD street props — fire hydrant, trash cans, lamp posts (geometry only;
 * LIGHT owns the actual lamps), and the phone booth at (0, 0, -35), the
 * accuse point.
 */
import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry
} from 'three'
import {
  COLOR_BOOTH_FRAME,
  COLOR_DARK_METAL,
  COLOR_GLASS,
  COLOR_HYDRANT_RED,
  COLOR_TRASH_CAN,
  LAMP_POST_HEIGHT,
  LAMP_POST_ZS,
  PHONE_BOOTH_ID,
  PHONE_BOOTH_POSITION,
  SIDEWALK_CENTER_X,
  SIDEWALK_HEIGHT
} from './constants'
import { enableShadows, interactRadius, tagInteract, tagSolid } from './helpers'

const METAL_MATERIAL = new MeshStandardMaterial({
  color: COLOR_DARK_METAL,
  roughness: 0.45,
  metalness: 0.8
})

const GLASS_MATERIAL = new MeshStandardMaterial({
  color: COLOR_GLASS,
  roughness: 0.1,
  metalness: 0.2,
  transparent: true,
  opacity: 0.22
})

const createLampPost = (x: number, z: number): Group => {
  const group = new Group()
  group.name = 'lamp-post'
  const pole = new Mesh(new CylinderGeometry(0.06, 0.09, LAMP_POST_HEIGHT, 8), METAL_MATERIAL)
  pole.position.y = LAMP_POST_HEIGHT / 2
  enableShadows(pole)
  tagSolid(pole)
  group.add(pole)

  const armLength = 1.2
  const armDirection = x < 0 ? 1 : -1
  const arm = new Mesh(new CylinderGeometry(0.045, 0.045, armLength, 6), METAL_MATERIAL)
  arm.rotation.z = Math.PI / 2
  arm.position.set((armDirection * armLength) / 2, LAMP_POST_HEIGHT - 0.1, 0)
  enableShadows(arm)
  group.add(arm)

  const headMaterial = new MeshStandardMaterial({
    color: 0xb9b39a,
    roughness: 0.35,
    metalness: 0.1
  })
  const head = new Mesh(new SphereGeometry(0.18, 10, 8), headMaterial)
  head.position.set(armDirection * armLength, LAMP_POST_HEIGHT - 0.25, 0)
  group.add(head)

  group.position.set(x, SIDEWALK_HEIGHT, z)
  return group
}

const createHydrant = (x: number, z: number): Group => {
  const group = new Group()
  group.name = 'fire-hydrant'
  const material = new MeshStandardMaterial({
    color: COLOR_HYDRANT_RED,
    roughness: 0.55,
    metalness: 0.25
  })
  const body = new Mesh(new CylinderGeometry(0.16, 0.2, 0.6, 10), material)
  body.position.y = 0.3
  enableShadows(body)
  tagSolid(body)
  group.add(body)
  const dome = new Mesh(new SphereGeometry(0.16, 10, 8, 0, Math.PI * 2, 0, Math.PI / 2), material)
  dome.position.y = 0.6
  group.add(dome)
  const sides: readonly number[] = [-1, 1]
  sides.forEach((side) => {
    const cap = new Mesh(new CylinderGeometry(0.07, 0.07, 0.12, 8), material)
    cap.rotation.x = Math.PI / 2
    cap.position.set(0, 0.42, side * 0.2)
    group.add(cap)
  })
  group.position.set(x, SIDEWALK_HEIGHT, z)
  return group
}

const createTrashCan = (x: number, z: number, tippedAngle: number): Group => {
  const group = new Group()
  group.name = 'trash-can'
  const material = new MeshStandardMaterial({
    color: COLOR_TRASH_CAN,
    roughness: 0.6,
    metalness: 0.7
  })
  const body = new Mesh(new CylinderGeometry(0.3, 0.26, 0.85, 12), material)
  body.position.y = 0.425
  enableShadows(body)
  tagSolid(body)
  group.add(body)
  const lid = new Mesh(new CylinderGeometry(0.33, 0.33, 0.07, 12), material)
  lid.position.y = 0.88
  enableShadows(lid)
  group.add(lid)
  group.rotation.z = tippedAngle
  group.position.set(x, tippedAngle === 0 ? 0 : 0.12, z)
  return group
}

/**
 * A silhouetted figure — trench coat, fedora — standing motionless in a
 * streetlamp pool. The eye reaches the light and finds someone waiting
 * (QA round 2: every pool was empty). Near-black matte so it reads as a
 * cut-out silhouette against the lit road, never a modeled character.
 */
const createLonelyFigure = (x: number, z: number, facingYaw: number): Group => {
  const group = new Group()
  group.name = 'lonely-figure'
  const silhouette = new MeshStandardMaterial({
    color: 0x0a0b0d,
    roughness: 0.95,
    metalness: 0
  })

  // Trench coat: flared truncated cone up to the shoulders.
  const coat = new Mesh(new CylinderGeometry(0.22, 0.34, 1.25, 12), silhouette)
  coat.position.y = 0.625
  enableShadows(coat)
  tagSolid(coat)
  group.add(coat)

  // Shoulders + collar.
  const shoulders = new Mesh(new CylinderGeometry(0.16, 0.24, 0.25, 12), silhouette)
  shoulders.position.y = 1.37
  enableShadows(shoulders)
  group.add(shoulders)

  const head = new Mesh(new SphereGeometry(0.11, 10, 8), silhouette)
  head.position.y = 1.6
  enableShadows(head)
  group.add(head)

  // Fedora: wide brim + crown.
  const brim = new Mesh(new CylinderGeometry(0.21, 0.21, 0.025, 14), silhouette)
  brim.position.y = 1.68
  enableShadows(brim)
  group.add(brim)
  const crown = new Mesh(new CylinderGeometry(0.1, 0.13, 0.14, 12), silhouette)
  crown.position.y = 1.75
  enableShadows(crown)
  group.add(crown)

  group.rotation.y = facingYaw
  group.position.set(x, 0, z)
  return group
}

const createPhoneBooth = (): Group => {
  const group = new Group()
  group.name = 'phone-booth'
  const frameMaterial = new MeshStandardMaterial({
    color: COLOR_BOOTH_FRAME,
    roughness: 0.5,
    metalness: 0.4
  })
  const boothWidth = 1.1
  const boothHeight = 2.5
  const half = boothWidth / 2

  const base = new Mesh(new BoxGeometry(boothWidth, 0.12, boothWidth), frameMaterial)
  base.position.y = 0.06
  enableShadows(base)
  tagSolid(base)
  group.add(base)

  const roof = new Mesh(new BoxGeometry(boothWidth + 0.1, 0.18, boothWidth + 0.1), frameMaterial)
  roof.position.y = boothHeight - 0.09
  enableShadows(roof)
  tagSolid(roof)
  group.add(roof)

  const cornerOffsets: ReadonlyArray<readonly [number, number]> = [
    [-half, -half],
    [-half, half],
    [half, -half],
    [half, half]
  ]
  cornerOffsets.forEach(([cx, cz]) => {
    const post = new Mesh(new BoxGeometry(0.09, boothHeight, 0.09), frameMaterial)
    post.position.set(cx, boothHeight / 2, cz)
    enableShadows(post)
    tagSolid(post)
    group.add(post)
  })

  // Glass panes on three sides; open on +z (facing the street toward spawn).
  const paneSpecs: ReadonlyArray<readonly [number, number, number, number]> = [
    // [x, z, rotationY, width]
    [0, -half, 0, boothWidth],
    [-half, 0, Math.PI / 2, boothWidth],
    [half, 0, Math.PI / 2, boothWidth]
  ]
  paneSpecs.forEach(([px, pz, rotationY, width]) => {
    const pane = new Mesh(new BoxGeometry(width - 0.12, boothHeight - 0.5, 0.03), GLASS_MATERIAL)
    pane.rotation.y = rotationY
    pane.position.set(px, boothHeight / 2, pz)
    tagSolid(pane)
    group.add(pane)
  })

  const phone = new Mesh(new BoxGeometry(0.28, 0.5, 0.14), METAL_MATERIAL)
  phone.position.set(0, 1.45, -half + 0.12)
  group.add(phone)

  tagInteract(group, {
    id: PHONE_BOOTH_ID,
    kind: 'prop',
    prompt: 'Make the call',
    radius: interactRadius()
  })
  tagInteract(phone, {
    id: PHONE_BOOTH_ID,
    kind: 'prop',
    prompt: 'Make the call',
    radius: interactRadius()
  })

  group.position.set(PHONE_BOOTH_POSITION.x, PHONE_BOOTH_POSITION.y, PHONE_BOOTH_POSITION.z)
  return group
}

export const buildProps = (): Group => {
  const props = new Group()
  props.name = 'street-props'
  LAMP_POST_ZS.forEach((z) => {
    props.add(createLampPost(-(SIDEWALK_CENTER_X - 1.3), z))
  })
  LAMP_POST_ZS.filter((z) => Math.abs(z) > 4).forEach((z) => {
    props.add(createLampPost(SIDEWALK_CENTER_X - 1.3, z + 7))
  })
  props.add(createHydrant(SIDEWALK_CENTER_X - 1, 20))
  // Under the lamp pool at (-5.5, 15) — the street-wide shot's near cone.
  props.add(createLonelyFigure(-5.35, 14.4, 0.6))
  props.add(createTrashCan(SIDEWALK_CENTER_X - 0.8, -15, 0))
  props.add(createTrashCan(SIDEWALK_CENTER_X - 1.4, -16.2, Math.PI / 2.2))
  props.add(createTrashCan(-(SIDEWALK_CENTER_X - 0.9), 8, 0))
  props.add(createPhoneBooth())
  return props
}
